import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CreditCard, Hotel, Wrench, MapPin } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const BRANDS = [
  { name: 'Shell', localSvg: 'shell', coverage: '~1.900 Stationen in Deutschland · dichtes Autobahn-Netz' },
  { name: 'Aral', localSvg: 'aral', coverage: '~2.400 Stationen · größtes Tankstellennetz Deutschlands' },
  { name: 'BP', localSvg: 'bp', coverage: '8.000+ Stationen europaweit' },
  { name: 'Esso', localSvg: 'esso', coverage: '~1.000 Stationen in Deutschland' },
  { name: 'TotalEnergies', localSvg: 'totalenergies', coverage: '~1.100 Stationen in Deutschland' },
  { name: 'JET', localSvg: 'jet', coverage: '~800 Stationen · oft besonders günstige Literpreise' },
  { name: 'OMV', localSvg: 'omv', coverage: '~2.100 Stationen in Zentraleuropa · stark in Süddeutschland' },
  { name: 'Repsol', localSvg: 'repsol', coverage: '~3.300 Stationen in Spanien & Portugal · ideal im Urlaub' },
  { name: 'Orlen', localSvg: 'orlen', coverage: '~600 star Stationen in Deutschland' },
  { name: 'Q1', localSvg: 'q1', coverage: '~200 Stationen in Deutschland' },
];

/** Kompakter Marken-Strip: klickbare Logos mit Mini-Info, wie dort eingelöst wird. */
export default function FuelBrandsStrip({ testId = 'fuel-brands-strip' }) {
  return (
    <section className="py-9 sm:py-11 bg-white/60 border-y border-stone-100" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-12">
          <div className="flex-shrink-0 lg:max-w-[220px]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-700">Prepaid-Guthaben einlösbar</p>
            <p className="text-[13px] text-stone-500 mt-1 leading-snug">an 150.000+ Tankstellen in Europa — Logo antippen für Details</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-4 flex-1" data-testid="fuel-brands-logos">
            {BRANDS.map((b) => (
              <Popover key={b.name}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="grayscale opacity-60 hover:grayscale-0 hover:opacity-100 data-[state=open]:grayscale-0 data-[state=open]:opacity-100 transition-all duration-300 rounded-lg px-1.5 py-1 hover:bg-white data-[state=open]:bg-white data-[state=open]:shadow-sm cursor-pointer"
                    aria-label={`Einlöse-Info für ${b.name}`}
                    data-testid={`fuel-brand-btn-${b.name.toLowerCase()}`}
                  >
                    <BrandLogo name={b.name} localSvg={b.localSvg} size={30} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 rounded-2xl border-stone-200 shadow-xl" sideOffset={10} data-testid="fuel-brand-popover">
                  <div className="p-4 border-b border-stone-100 flex items-center gap-3">
                    <BrandLogo name={b.name} localSvg={b.localSvg} size={26} />
                    <div>
                      <p className="text-sm font-semibold text-stone-900 leading-tight">{b.name}</p>
                      <p className="text-[11px] text-stone-500 mt-0.5 flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-[1px] flex-shrink-0 text-emerald-600" strokeWidth={2} />{b.coverage}
                      </p>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-2.5">So löst du hier ein</p>
                    <ol className="space-y-2">
                      <li className="flex items-start gap-2.5 text-[12px] text-stone-600 leading-snug">
                        <span className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0"><Hotel className="w-3 h-3 text-stone-700" strokeWidth={2} /></span>
                        <span><strong className="text-stone-900">Guthaben aufladen:</strong> über FuelX Hotelbuchungen oder Kfz-Services wird dein FuelGood-Guthaben auf die Prepaid-Tankkarte übertragen.</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-[12px] text-stone-600 leading-snug">
                        <span className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0"><CreditCard className="w-3 h-3 text-stone-700" strokeWidth={2} /></span>
                        <span><strong className="text-stone-900">An der Kasse zahlen:</strong> tanken wie immer und mit der FuelX Prepaid-Tankkarte kontaktlos bezahlen — bei {b.name} und jeder anderen Marke.</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-[12px] text-stone-600 leading-snug">
                        <span className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0"><Wrench className="w-3 h-3 text-stone-700" strokeWidth={2} /></span>
                        <span><strong className="text-stone-900">Auch abseits der Zapfsäule:</strong> Guthaben gilt ebenso für berechtigte Kfz-Services wie TÜV, Reifen oder Ölwechsel.</span>
                      </li>
                    </ol>
                    <p className="text-[10.5px] text-stone-500 mt-3 leading-snug">
                      Voraussetzung: Karte enthält Guthaben aus Hotelbuchungen oder Kfz-Services. Keine Barauszahlung.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            ))}
          </div>
          <Link
            to="/tankstellen-netzwerk"
            className="hidden lg:inline-flex items-center gap-1.5 text-[12.5px] font-medium text-stone-500 hover:text-stone-900 transition-colors flex-shrink-0"
            data-testid="fuel-brands-link"
          >
            Ganzes Netzwerk <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </section>
  );
}
